import { useState, useCallback } from 'react';
import { useSubmitHighscore } from '@/hooks/useHighscores';
import { useTranslation } from '@/i18n';
import { GameScreen } from './GameScreen';
import { StartModal } from './StartModal';
import { EndModal } from './EndModal';
import { GameHUD } from './GameHUD';
import type { GameState } from './types';

const GAME_ID = 'automation-drop';

const INITIAL_STATE: GameState = {
  status: 'idle',
  score: 0,
  placedCount: 0,
  totalParts: 0,
  missesCount: 0,
  level: 0,
  speedStage: 1,
  isPaused: false,
};

export function AutomationDropGame() {
  const [gameState, setGameState] = useState<GameState>(INITIAL_STATE);
  const [showStartModal, setShowStartModal] = useState(true);
  const [showEndModal, setShowEndModal] = useState(false);
  const submitHighscore = useSubmitHighscore();

  const handleStart = useCallback((startLevel: number = 0) => {
    setShowStartModal(false);
    setShowEndModal(false);
    setGameState({
      ...INITIAL_STATE,
      status: 'playing',
      level: startLevel,
    });
  }, []);

  const handleGameEnd = useCallback((finalState: GameState, _won: boolean) => {
    setGameState({ ...finalState, status: 'ended' });
    setShowEndModal(true);
  }, []);

  const handleScoreSubmit = useCallback(async (name: string, email?: string) => {
    await submitHighscore.mutateAsync({
      game_id: GAME_ID,
      player_name: name,
      email: email || undefined,
      score: gameState.score,
      level: gameState.level,
      metadata: {
        placedCount: gameState.placedCount,
        missesCount: gameState.missesCount,
        speedStage: gameState.speedStage,
      },
    });
  }, [gameState, submitHighscore]);

  const handlePlayAgain = useCallback(() => {
    setShowEndModal(false);
    setShowStartModal(true);
    setGameState(INITIAL_STATE);
  }, []);

  const handlePause = useCallback(() => {
    setGameState(prev => ({ ...prev, isPaused: !prev.isPaused }));
  }, []);

  const handleMenu = useCallback(() => {
    setGameState(prev => ({ ...prev, status: 'ended' }));
    setShowEndModal(false);
    setShowStartModal(true);
  }, []);

  const updateGameState = useCallback((updates: Partial<GameState>) => {
    setGameState(prev => ({ ...prev, ...updates }));
  }, []);

  return (
    <div className="min-h-screen bg-bg-deep flex items-center justify-center p-4">
      <div className="game-container">
        {/* Game HUD */}
        {gameState.status === 'playing' && (
          <GameHUD 
            gameState={gameState} 
            onPause={handlePause}
            onMenu={handleMenu}
          />
        )}

        {/* Game Screen */}
        <GameScreen
          gameState={gameState}
          onGameEnd={handleGameEnd}
          updateGameState={updateGameState}
        />

        {/* Pause Overlay */}
        {gameState.isPaused && gameState.status === 'playing' && (
          <PauseOverlay 
            onResume={handlePause} 
            onMenu={handleMenu}
          />
        )}

        {/* Modals */}
        {showStartModal && (
          <StartModal onStart={handleStart} />
        )}

        {showEndModal && (
          <EndModal
            gameState={gameState}
            onSubmit={handleScoreSubmit}
            onPlayAgain={handlePlayAgain}
            isSubmitting={submitHighscore.isPending}
          />
        )}
      </div>
    </div>
  );
}

function PauseOverlay({ 
  onResume, 
  onMenu,
}: { 
  onResume: () => void; 
  onMenu: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="absolute inset-0 bg-black/50 flex items-end justify-center pb-10 z-50">
      <div className="card p-6 min-w-[320px] animate-slide-up">
        <h2 className="text-2xl font-bold text-text-primary mb-4 text-center">{t.common.pause.toUpperCase()}</h2>
        <div className="flex gap-4">
          <button onClick={onResume} className="btn-primary flex-1">{t.common.resume}</button>
          <button onClick={onMenu} className="btn-secondary flex-1">{t.common.menu}</button>
        </div>
      </div>
    </div>
  );
}
